import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { nomenclature as nomenclatureApi } from '@/api/catalogs';
import {
  nomenclatureSpecifications,
  type NomenclatureSpecificationListItem,
} from '@/api/nomenclatureSpecifications';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { DocListFiltersRow } from '@/shared/components/DocListFilters';
import { useOrgNames, dtOrgCol } from '@/shared/hooks/useOrgNames';
import { useDefaults } from '@/store/defaults';

const PERMISSION = 'production.specification.manage';

export default function NomenclatureSpecificationsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  // Організація: слідуємо за дефолтом шапки, поки не обрано вручну
  // (канон useDocListFilters; період специфікаціям не потрібен — довідник).
  const defaultOrgId = useDefaults((st) => st.defaultOrgId);
  const [orgOverride, setOrgOverride] = useState<string | null>(null);
  const orgId = orgOverride ?? defaultOrgId;
  const orgNames = useOrgNames();

  const list = useQuery({
    queryKey: ['nomenclature-specifications', orgId],
    queryFn: () =>
      nomenclatureSpecifications.list({ organization_id: orgId || undefined, limit: 500 }),
  });

  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomenclatureApi.listAll(),
  });
  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  const cols = useMemo<ColumnDef<NomenclatureSpecificationListItem>[]>(
    () => [
      {
        key: 'product_nomenclature_id',
        header: 'Продукція',
        sortValue: (r) => nomName.get(r.product_nomenclature_id) ?? '',
        cell: (r) => nomName.get(r.product_nomenclature_id) ?? '',
      },
      {
        key: 'name',
        header: 'Назва специфікації',
        sortValue: (r) => r.name ?? '',
        cell: (r) => r.name,
      },
      dtOrgCol(orgNames),
      {
        key: 'base_quantity',
        header: 'Базова к-сть',
        width: 130,
        sortValue: (r) => Number(r.base_quantity),
        cell: (r) => r.base_quantity,
      },
      {
        key: 'is_active',
        header: 'Активна',
        width: 110,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
      {
        key: 'valid_from',
        header: 'Діє з',
        width: 160,
        sortValue: (r) => r.valid_from ?? '',
        cell: (r) => (r.valid_from ? new Date(r.valid_from).toLocaleDateString('uk-UA') : ''),
      },
    ],
    [nomName, orgNames],
  );

  const canCreate = orgId ? allowedOrgIds.has(orgId) : perms.availableOrgs.length > 0;
  const tooltipNew = !canCreate
    ? `Немає прав створювати специфікації у цій юрособі (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
    : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Специфікації</h2>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/nomenclature-specifications/new')}
        >
          + Нова специфікація
        </button>
      </div>
      <DocListFiltersRow>
        <select value={orgId} onChange={(e) => setOrgOverride(e.target.value)}
          style={{ width: 160, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
      </DocListFiltersRow>
      <DataTable<NomenclatureSpecificationListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає специфікацій."
        onRowDoubleClick={(r) => navigate(`/nomenclature-specifications/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="name"
      />
    </div>
  );
}
